import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Activity, 
  Zap, 
  Users, 
  Shield, 
  TrendingUp, 
  TrendingDown,
  RefreshCw,
  Server,
  Globe,
  Database,
  Cpu,
  HardDrive,
  Wifi,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  BarChart3,
  PieChart,
  LineChart,
  Monitor,
  Network,
  Gauge,
  Bell,
  Settings,
  Eye,
  Trash2,
  Filter,
  Search,
  Calendar,
  Info,
  Download
} from 'lucide-react';

interface NetworkMetrics {
  blockHeight: number;
  blockTime: number;
  tps: number;
  gasPrice: number;
  networkHashrate: string;
  difficulty: string;
  totalSupply: string;
  circulatingSupply: string;
  marketCap: number;
  activeValidators: number;
  totalStaked: string;
  stakingRatio: number;
  networkUptime: number;
  lastBlockTime: string;
}

interface NodeStatus {
  id: string;
  name: string;
  type: 'validator' | 'rpc' | 'api' | 'indexer';
  status: 'online' | 'offline' | 'syncing' | 'error';
  uptime: number;
  lastSeen: string;
  version: string;
  location: string;
  latency: number;
  blockHeight: number;
  peers: number;
  cpuUsage: number;
  memoryUsage: number;
  diskUsage: number;
}

interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'warning' | 'error' | 'success';
  timestamp: string;
  read: boolean;
  category: 'system' | 'security' | 'network' | 'user';
  priority: 'low' | 'medium' | 'high' | 'critical';
}

interface AnalyticsData {
  period: string;
  transactions: number;
  volume: number;
  activeUsers: number;
  gasUsed: number;
  blockTime: number;
  networkLoad: number;
  location: string;
  version: string;
  uptime: number;
  latency: number;
  blockHeight: number;
  peers: number;
  lastSeen: string;
  cpuUsage: number;
  memoryUsage: number;
  diskUsage: number;
}

interface ChainInfo {
  chainId: number;
  networkName: string;
  consensusAlgorithm: string;
  blockTime: number;
  maxBlockSize: string;
  gasLimit: number;
  baseFee: number;
  forkHeight?: number;
  upgradeVersion?: string;
}

const NetworkStatusPage: React.FC = () => {
  const [metrics, setMetrics] = useState<NetworkMetrics | null>(null);
  const [nodes, setNodes] = useState<NodeStatus[]>([]);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [analytics, setAnalytics] = useState<AnalyticsData[]>([]);
  const [chainInfo, setChainInfo] = useState<ChainInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  const [selectedTimeframe, setSelectedTimeframe] = useState<'1h' | '24h' | '7d' | '30d'>('24h');
  const [activeTab, setActiveTab] = useState<'overview' | 'nodes' | 'chain'>('overview');
  const [notificationFilter, setNotificationFilter] = useState('all');
  const [analyticsTimeframe, setAnalyticsTimeframe] = useState('24h');

  useEffect(() => {
    fetchNetworkStatus();
    const interval = setInterval(fetchNetworkStatus, 10000); // 10초마다 업데이트
    return () => clearInterval(interval);
  }, [selectedTimeframe]);

  const fetchNetworkStatus = async () => {
    try {
      setLoading(true);
      
      // Mock 네트워크 메트릭스
      const mockMetrics: NetworkMetrics = {
        blockHeight: 12345678,
        blockTime: 2.1,
        tps: 2847,
        gasPrice: 25.5,
        networkHashrate: '1.2 TH/s',
        difficulty: '15.7T',
        totalSupply: '3000000000',
        circulatingSupply: '5000000',
        marketCap: 4270500000,
        activeValidators: 47,
        totalStaked: '850000000',
        stakingRatio: 56.7,
        networkUptime: 99.97,
        lastBlockTime: new Date().toISOString()
      };
      
      // Mock 노드 상태
      const mockNodes: NodeStatus[] = [
        {
          id: 'node-1',
          name: 'Voltron-Validator-01',
          type: 'validator',
          status: 'online',
          location: 'US-East-1',
          version: 'v1.10.0',
          uptime: 99.8,
          latency: 45,
          blockHeight: 12345678,
          peers: 156,
          lastSeen: new Date().toISOString(),
          cpuUsage: 35.2,
          memoryUsage: 68.5,
          diskUsage: 42.1
        },
        {
          id: 'node-2',
          name: 'VOLTRON-Validator-01',
          type: 'validator',
          status: 'online',
          location: 'EU-West-1',
          version: 'v1.10.0',
          uptime: 99.5,
          latency: 78,
          blockHeight: 12345678,
          peers: 142,
          lastSeen: new Date(Date.now() - 30000).toISOString(),
          cpuUsage: 42.8,
          memoryUsage: 71.3,
          diskUsage: 38.7
        },
        {
          id: 'node-3',
          name: 'BSS-Validator-01',
          type: 'validator',
          status: 'syncing',
          location: 'Asia-Pacific-1',
          version: 'v1.9.5',
          uptime: 98.9,
          latency: 125,
          blockHeight: 12345675,
          peers: 98,
          lastSeen: new Date(Date.now() - 60000).toISOString(),
          cpuUsage: 78.4,
          memoryUsage: 85.2,
          diskUsage: 67.3
        },
        {
          id: 'node-4',
          name: 'RPC-Gateway-01',
          type: 'rpc',
          status: 'online',
          location: 'US-West-1',
          version: 'v1.10.0',
          uptime: 99.9,
          latency: 32,
          blockHeight: 12345678,
          peers: 234,
          lastSeen: new Date().toISOString(),
          cpuUsage: 28.6,
          memoryUsage: 45.8,
          diskUsage: 23.4
        },
        {
          id: 'node-5',
          name: 'API-Server-01',
          type: 'api',
          status: 'online',
          location: 'EU-Central-1',
          version: 'v1.10.0',
          uptime: 99.6,
          latency: 56,
          blockHeight: 12345678,
          peers: 0,
          lastSeen: new Date().toISOString(),
          cpuUsage: 15.3,
          memoryUsage: 32.7,
          diskUsage: 18.9
        },
        {
          id: 'node-6',
          name: 'Indexer-01',
          type: 'indexer',
          status: 'error',
          location: 'Asia-East-1',
          version: 'v1.9.8',
          uptime: 95.2,
          latency: 0,
          blockHeight: 12345650,
          peers: 0,
          lastSeen: new Date(Date.now() - 300000).toISOString(),
          cpuUsage: 0,
          memoryUsage: 0,
          diskUsage: 89.7
        }
      ];
      
      // 실제 체인 정보 조회
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '/api';
      let chainInfoData: ChainInfo | null = null;
      
      try {
        const response = await fetch(`${apiBaseUrl}/explorer/network-stats`);
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.stats) {
            chainInfoData = {
              chainId: 88002, // Voltron Mainnet Chain ID
              networkName: 'Voltron Mainnet',
              consensusAlgorithm: 'Polygon Edge (IBFT 2.0)',
              blockTime: data.stats.blockTime || 2.0,
              maxBlockSize: '2MB',
              gasLimit: 30000000,
              baseFee: parseFloat(data.stats.gasPriceGwei || '0'),
              forkHeight: 0,
              upgradeVersion: 'v1.0.0'
            };
          }
        }
      } catch (error) {
        console.warn('체인 정보 조회 실패, 기본값 사용:', error);
      }
      
      // 기본값
      const mockChainInfo: ChainInfo = chainInfoData || {
        chainId: 88002,
        networkName: 'Voltron Mainnet',
        consensusAlgorithm: 'Polygon Edge (IBFT 2.0)',
        blockTime: 2.0,
        maxBlockSize: '2MB',
        gasLimit: 30000000,
        baseFee: 0,
        forkHeight: 0,
        upgradeVersion: 'v1.0.0'
      };
      
      setMetrics(mockMetrics);
      setNodes(mockNodes);
      setChainInfo(mockChainInfo);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch network status:', error);
      setLoading(false);
    }
  };

  const formatNumber = (num: number | string) => {
    const n = typeof num === 'string' ? parseFloat(num) : num;
    if (n >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
    if (n >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
    if (n >= 1e3) return `${(n / 1e3).toFixed(2)}K`;
    return n.toFixed(0);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'online':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'offline':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'syncing':
        return <RefreshCw className="w-4 h-4 text-yellow-500 animate-spin" />;
      case 'error':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      default:
        return <AlertCircle className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online': return 'bg-green-100 text-green-800';
      case 'offline': return 'bg-red-100 text-red-800';
      case 'syncing': return 'bg-yellow-100 text-yellow-800';
      case 'error': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'validator': return 'bg-purple-100 text-purple-800';
      case 'rpc': return 'bg-blue-100 text-blue-800';
      case 'api': return 'bg-green-100 text-green-800';
      case 'indexer': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getHealthColor = (value: number, type: 'uptime' | 'usage') => {
    if (type === 'uptime') {
      if (value >= 99) return 'text-green-600';
      if (value >= 95) return 'text-yellow-600';
      return 'text-red-600';
    } else {
      if (value <= 50) return 'text-green-600';
      if (value <= 80) return 'text-yellow-600';
      return 'text-red-600';
    }
  };

  const formatTimeAgo = (timestamp: string) => {
    const seconds = Math.floor((Date.now() - new Date(timestamp).getTime()) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* 헤더 */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold gradient-text mb-2">Network Status</h1>
          <p className="text-slate-600">Real-time network health and performance monitoring</p>
        </div>
        <div className="flex space-x-3">
          <select
            value={selectedTimeframe}
            onChange={(e) => setSelectedTimeframe(e.target.value as '1h' | '24h' | '7d' | '30d')}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
          >
            <option value="1h">Last Hour</option>
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
          </select>
          <button
            onClick={fetchNetworkStatus}
            className="flex items-center space-x-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* 탭 네비게이션 */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'overview'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Network Overview
          </button>
          <button
            onClick={() => setActiveTab('nodes')}
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'nodes'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Node Status
          </button>
          <button
            onClick={() => setActiveTab('chain')}
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'chain'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Chain Info
          </button>
        </div>
      </div>

      {activeTab === 'overview' && metrics && (
        <>
          {/* 핵심 메트릭스 */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Block Height</p>
                  <p className="text-2xl font-bold text-gray-900">{metrics.blockHeight.toLocaleString()}</p>
                  <p className="text-xs text-gray-500 mt-1">Latest: {formatTimeAgo(metrics.lastBlockTime)}</p>
                </div>
                <Activity className="w-8 h-8 text-orange-500" />
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Network TPS</p>
                  <p className="text-2xl font-bold text-blue-600">{metrics.tps.toLocaleString()}</p>
                  <p className="text-xs text-gray-500 mt-1">Block time: {metrics.blockTime}s</p>
                </div>
                <Zap className="w-8 h-8 text-blue-500" />
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Active Validators</p>
                  <p className="text-2xl font-bold text-purple-600">{metrics.activeValidators}</p>
                  <p className="text-xs text-gray-500 mt-1">Uptime: {metrics.networkUptime}%</p>
                </div>
                <Shield className="w-8 h-8 text-purple-500" />
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Gas Price</p>
                  <p className="text-2xl font-bold text-green-600">{metrics.gasPrice} Gwei</p>
                  <p className="text-xs text-gray-500 mt-1">Network fee</p>
                </div>
                <Gauge className="w-8 h-8 text-green-500" />
              </div>
            </div>
          </div>

          {/* 상세 메트릭스 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* 네트워크 통계 */}
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <BarChart3 className="w-5 h-5 mr-2 text-orange-500" />
                Network Statistics
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Total Supply</span>
                  <span className="text-sm font-medium text-gray-900">{formatNumber(metrics.totalSupply)} VTTN</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Circulating Supply</span>
                  <span className="text-sm font-medium text-gray-900">{formatNumber(metrics.circulatingSupply)} VTTN</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Market Cap</span>
                  <span className="text-sm font-medium text-gray-900">${formatNumber(metrics.marketCap)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Total Staked</span>
                  <span className="text-sm font-medium text-gray-900">{formatNumber(metrics.totalStaked)} VTTN</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Staking Ratio</span>
                  <span className="text-sm font-medium text-gray-900">{metrics.stakingRatio}%</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Network Hashrate</span>
                  <span className="text-sm font-medium text-gray-900">{metrics.networkHashrate}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Difficulty</span>
                  <span className="text-sm font-medium text-gray-900">{metrics.difficulty}</span>
                </div>
              </div>
            </div>

            {/* 네트워크 상태 */}
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Monitor className="w-5 h-5 mr-2 text-orange-500" />
                Network Health
              </h3>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-gray-600">Network Uptime</span>
                    <span className={`text-sm font-medium ${getHealthColor(metrics.networkUptime, 'uptime')}`}>
                      {metrics.networkUptime}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-green-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${metrics.networkUptime}%` }}
                    ></div>
                  </div>
                </div>
                
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-gray-600">Staking Participation</span>
                    <span className="text-sm font-medium text-purple-600">{metrics.stakingRatio}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-purple-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${metrics.stakingRatio}%` }}
                    ></div>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 pt-4">
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">{nodes.filter(n => n.status === 'online').length}</div>
                    <div className="text-xs text-green-600">Online Nodes</div>
                  </div>
                  <div className="text-center p-3 bg-red-50 rounded-lg">
                    <div className="text-2xl font-bold text-red-600">{nodes.filter(n => n.status === 'offline' || n.status === 'error').length}</div>
                    <div className="text-xs text-red-600">Offline Nodes</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {activeTab === 'nodes' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Server className="w-5 h-5 mr-2 text-orange-500" />
              Node Infrastructure Status
            </h3>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Node</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Version</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Uptime</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Performance</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Seen</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-12 text-center">
                      <div className="flex justify-center items-center space-x-2">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500"></div>
                        <span className="text-gray-500">Loading nodes...</span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  nodes.map((node) => (
                    <tr key={node.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{node.name}</div>
                          <div className="text-sm text-gray-500">Block: {node.blockHeight.toLocaleString()}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          {getStatusIcon(node.status)}
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(node.status)}`}>
                            {node.status}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTypeColor(node.type)}`}>
                          {node.type}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {node.location}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {node.version}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <span className={`text-sm font-medium ${getHealthColor(node.uptime, 'uptime')}`}>
                            {node.uptime.toFixed(1)}%
                          </span>
                          <div className="w-16 bg-gray-200 rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full ${
                                node.uptime >= 99 ? 'bg-green-500' :
                                node.uptime >= 95 ? 'bg-yellow-500' : 'bg-red-500'
                              }`}
                              style={{ width: `${Math.min(node.uptime, 100)}%` }}
                            ></div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-xs space-y-1">
                          <div className="flex justify-between">
                            <span>CPU:</span>
                            <span className={getHealthColor(node.cpuUsage, 'usage')}>{node.cpuUsage.toFixed(1)}%</span>
                          </div>
                          <div className="flex justify-between">
                            <span>RAM:</span>
                            <span className={getHealthColor(node.memoryUsage, 'usage')}>{node.memoryUsage.toFixed(1)}%</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Disk:</span>
                            <span className={getHealthColor(node.diskUsage, 'usage')}>{node.diskUsage.toFixed(1)}%</span>
                          </div>
                          {node.latency > 0 && (
                            <div className="flex justify-between">
                              <span>Ping:</span>
                              <span className="text-gray-600">{node.latency}ms</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center space-x-1">
                          <Clock className="w-4 h-4" />
                          <span>{formatTimeAgo(node.lastSeen)}</span>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'chain' && chainInfo && (
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
            <Network className="w-5 h-5 mr-2 text-orange-500" />
            Blockchain Configuration
          </h3>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="space-y-4">
              <h4 className="text-md font-medium text-gray-900 mb-3">Network Parameters</h4>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-600">Chain ID</span>
                  <span className="text-sm font-medium text-gray-900">{chainInfo.chainId}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-600">Network Name</span>
                  <span className="text-sm font-medium text-gray-900">{chainInfo.networkName}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-600">Consensus Algorithm</span>
                  <span className="text-sm font-medium text-gray-900">{chainInfo.consensusAlgorithm}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-600">Block Time</span>
                  <span className="text-sm font-medium text-gray-900">{chainInfo.blockTime}s</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-600">Max Block Size</span>
                  <span className="text-sm font-medium text-gray-900">{chainInfo.maxBlockSize}</span>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <h4 className="text-md font-medium text-gray-900 mb-3">Gas & Fee Configuration</h4>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-600">Gas Limit</span>
                  <span className="text-sm font-medium text-gray-900">{chainInfo.gasLimit.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-600">Base Fee</span>
                  <span className="text-sm font-medium text-gray-900">{chainInfo.baseFee} Gwei</span>
                </div>
                {chainInfo.forkHeight && (
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-sm text-gray-600">Last Fork Height</span>
                    <span className="text-sm font-medium text-gray-900">{chainInfo.forkHeight.toLocaleString()}</span>
                  </div>
                )}
                {chainInfo.upgradeVersion && (
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-sm text-gray-600">Latest Version</span>
                    <span className="text-sm font-medium text-gray-900">{chainInfo.upgradeVersion}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="mt-8 p-4 bg-blue-50 rounded-lg">
            <div className="flex items-start space-x-3">
              <Globe className="w-5 h-5 text-blue-500 mt-0.5" />
              <div>
                <h5 className="text-sm font-medium text-blue-900">Network Information</h5>
                <p className="text-sm text-blue-700 mt-1">
                  Voltron Mainnet is an energy-focused blockchain network using Polygon Edge (IBFT 2.0) consensus for optimized performance and energy management.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default NetworkStatusPage;
